import * as React from 'react'
import { useState } from 'react'
import {
  Button,
  TextField,
  Theme,
  createStyles,
  makeStyles,
} from '@material-ui/core'
import { useHistory, useLocation } from 'react-router-dom'
import { Box, Card } from '@mui/material'
import {
  useNotify,
  useTranslate,
  Notification,
  useQuery,
  sanitizeFetchType,
  useSafeSetState,
} from 'react-admin'
import { ReactComponent as Logo } from '../svg/logotype.svg'
import { ResourceName } from '../../../core/ResourceName'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    resetWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundImage:
        'radial-gradient(circle at 50% 14em, #ED7C00 0%, #5f5449ff 50%, #373737 100%);',
    },
    resetBox: {
      marginTop: '-40vh',
      width: 365,
    },
    resetHeader: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '1em',
    },
    formField: {
      marginBottom: theme.spacing(2),
    },
    submitButton: {
      color: '#373737',
      backgroundColor: '#ED7C00',
      '&:hover': {
        backgroundColor: '#373737',
        color: '#ED7C00',
      },
    },
  }),
)

export function ResetPasswordForm({ ...props }) {
  const { passwordExpired } = props
  const location = useLocation()
  const history = useHistory()
  const classes = useStyles()
  const translate = useTranslate()
  const notify = useNotify()

  const params = new URLSearchParams(location.search)
  const userId = params.get('userId')
  const token = params.get('token')

  const [newPassword, setNewPassword] = useSafeSetState('')
  const [newPassword2, setNewPassword2] = useSafeSetState('')
  const [sendPasswordReset, setSendPasswordReset] = useState<boolean>(false)

  useQuery(
    {
      type: sanitizeFetchType(
        passwordExpired ? 'changePassword' : 'resetPassword',
      ),
      resource: ResourceName.CURRENT_USER,
      payload: passwordExpired
        ? { newPassword }
        : { userId, token, newPassword },
    },
    {
      enabled: !!newPassword && !!newPassword2 && sendPasswordReset,
      onFailure: () => {
        setSendPasswordReset(false)
        notify(
          translate(
            'common.api-error-handling.additionalErrorCode.DefaultError',
          ),
          'error',
          {},
          false,
          10000,
        )
      },
      onSuccess: (data) => {
        setSendPasswordReset(false)
        if (data?.data?.details?.[0]?.relatedData?.additionalErrorCode)
          notify(
            translate(
              `common.api-error-handling.additionalErrorCode.${data.data.details[0].relatedData.additionalErrorCode}`,
            ),
            'error',
            {},
            false,
            10000,
          )
        else {
          setNewPassword('')
          setNewPassword2('')
          notify(
            translate('resources.auth.password-reset.success'),
            'info',
            {},
            false,
            4500,
          )
          setTimeout(() => history.push('/login'), 5000)
          notify(
            translate('resources.auth.password-reset.please-log-in'),
            'info',
            {},
            false,
            5000,
          )
        }
      },
    },
  )

  const handleSubmit = () => {
    setSendPasswordReset(true)
  }

  return (
    <Card>
      <div className={classes.resetHeader} aria-label="SPORTDATA">
        <Logo style={{ height: 60 }} />
      </div>
      {(userId && token) || passwordExpired ? (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            padding: '0 1em 1em 1em',
          }}
        >
          <TextField
            className={classes.formField}
            variant="standard"
            type="password"
            label={translate('resources.auth.password-reset.new-password')}
            fullWidth
            margin="normal"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete={undefined}
          />
          <TextField
            className={classes.formField}
            variant="standard"
            type="password"
            label={translate('resources.auth.password-reset.repeat-password')}
            fullWidth
            margin="normal"
            value={newPassword2}
            onChange={(e) => setNewPassword2(e.target.value)}
            autoComplete={undefined}
            error={newPassword2 ? newPassword !== newPassword2 : false}
            helperText={
              newPassword2 && newPassword !== newPassword2
                ? translate(
                    'resources.auth.password-reset.errors.passwords-mismatch',
                  )
                : ''
            }
          />

          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={
              !newPassword || !newPassword2 || newPassword !== newPassword2
            }
            className={classes.submitButton}
          >
            {translate('translation:resources.auth.password-reset.submit')}
          </Button>
        </Box>
      ) : (
        <></>
      )}
    </Card>
  )
}

export const ResetPasswordView = (props: any) => {
  const classes = useStyles()

  return (
    <div className={classes.resetWrapper}>
      <div className={classes.resetBox}>
        <ResetPasswordForm {...props} />
        <Notification />
      </div>
    </div>
  )
}
